import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from './authService';
import { clearAuth, setUser } from './authslice';
import StockerLoading from '../../../../components/loader/StockerLoadingAnimation';

const CONFIG = {
  IDLE_TIMEOUT: 30 * 60 * 1000, // 30 minutes
  ACTIVITY_EVENTS: ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'],
};

const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState(null);

  const { isAuthenticated } = useSelector(state => state.auth);

  // Idle timeout handler
  const handleIdleTimeout = useCallback(() => {
    dispatch(clearAuth());
    window.dispatchEvent(new CustomEvent('auth-idle-timeout'));
    navigate('/login', {
      replace: true,
      state: { from: location.pathname, message: 'You were logged out due to inactivity' }
    });
  }, [dispatch, navigate, location.pathname]);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await authService.initialize();

        try {
          const userProfile = await authService.getUserProfile();
          if (userProfile) {
            dispatch(setUser(userProfile));
          } else {
            dispatch(clearAuth());
            return; // stop retrying if no session
          }
        } catch (profileError) {
          //console.log('[AuthProvider] No valid session found');
          dispatch(clearAuth());
          return; // prevent triggering refresh
        }
      } catch (initError) {
        console.warn('[AuthProvider] Initialization failed:', initError);
        setInitError('Failed to initialize authentication services');
        dispatch(clearAuth());
      } finally {
        setIsInitialized(true);
      }
    };

    initializeAuth();
  }, [dispatch]);

  // Idle timeout management
  useEffect(() => {
    if (!isAuthenticated) return;

    let idleTimer;
    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(handleIdleTimeout, CONFIG.IDLE_TIMEOUT);
    };

    const handleUserActivity = () => resetIdleTimer();

    CONFIG.ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    resetIdleTimer();

    return () => {
      clearTimeout(idleTimer);
      CONFIG.ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
    };
  }, [isAuthenticated, handleIdleTimeout]);

  // Handle global authentication errors
  useEffect(() => {
    const handleAuthError = (event) => {
      console.warn('[AuthProvider] Authentication error:', event.detail);
      dispatch(clearAuth());
      navigate('/login', {
        replace: true,
        state: { message: 'Authentication error. Please log in again.' }
      });
    };

    const handleIdleTimeoutEvent = () => {
      //console.log('[AuthProvider] Session expired due to inactivity');
    };

    window.addEventListener('auth-error', handleAuthError);
    window.addEventListener('auth-idle-timeout', handleIdleTimeoutEvent);

    return () => {
      window.removeEventListener('auth-error', handleAuthError);
      window.removeEventListener('auth-idle-timeout', handleIdleTimeoutEvent);
    };
  }, [dispatch, navigate]);

  // Show loading screen during initialization
  if (!isInitialized) {
    return (
      <StockerLoading
        retryCount={0}
        message={initError ? "Initializing (with warnings)" : "Initializing"}
      />
    );
  }

  return children;
};

export default AuthProvider;
